import React from 'react';
import Container from 'components/atomic/atoms/container';
import { ResponsiveContainer } from 'components/atomic/atoms/responsive-container';
import { Gallery } from 'components/atomic/organisms/gallery';
import { DefaultPlaceholder } from 'components/atomic/organisms/gallery/bento-states';
import { ComplianceOverlay } from 'ui/compliance';
import { validateVariant } from 'ui/utils';
import { useTenantId } from 'hooks/use-tenant-id';
import { getBrandIcon, brandIconTypes } from 'design-system/tokens/brand-icons';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import ShareSaveListing from 'components/swiper-carousel/components/share-save-listing';
import { GALLERY_VARIANTS, type GallerySectionProps } from './types';
import { createMobileCounterSlot, createDesktopSeeAllSlot } from './gallery-slots';
import styles from './style.module.scss';

/**
 * ListingGallerySection
 * 
 * Listing-specific wrapper around the Gallery organism.
 * Adds real estate domain concerns: compliance overlay, image counter with i18n.
 */
export function ListingGallerySection({
  sectionId,
  show = 'all',
  data,
  variant,
  onImageClick,
  onSlideChange,
  onSeeAllClick,
  blur,
  complianceOverlay,
  className,
  share,
}: GallerySectionProps) {
  const tenantId = useTenantId();
  const { t } = useTranslation(Namespaces.COMMON);

  if (!validateVariant('ListingGallerySection', variant, GALLERY_VARIANTS)) {
    return null;
  }

  const effectiveVariant = variant === GALLERY_VARIANTS.COMPACT ? GALLERY_VARIANTS.CAROUSEL : variant;
  const isBento = effectiveVariant === GALLERY_VARIANTS.BENTO_GRID;

  const complianceOverlayElement = blur && complianceOverlay ? (
    <ComplianceOverlay
      data={{
        shouldShow: true,
        message: complianceOverlay.message,
        ctaText: complianceOverlay.ctaText,
      }}
      onClick={complianceOverlay.onClick}
      borderRadius={isBento ? '24px' : 0}
      opacity={0}
    />
  ) : undefined;

  // Tenant-aware placeholder for when no images exist (via DI)
  const NoImageIconComponent = getBrandIcon(brandIconTypes.NO_IMAGE, tenantId);
  const placeholderSlot = (
    <DefaultPlaceholder
      icon={<NoImageIconComponent />}
      message={t(LOCALE_COMMON_PATHS.NO_IMAGE_PHOTOS_COMING_SOON, {
        defaultValue: 'Photos Coming Soon',
      })}
    />
  );

  // Share slot - only for carousel variant (domain layer can access tenantId)
  const shareSlot = share && !isBento ? (
    <ShareSaveListing
      showFavouriteToggle={false}
      id={share.listingId}
      text={share.text}
      url={share.url}
      isModal={false}
      tenantId={tenantId}
    />
  ) : undefined;

  return (
    <ResponsiveContainer
      show={show}
      as="section"
      className={className}
      data-section={sectionId}
      data-variant={variant}
    >
      <Container className={isBento ? styles.bentoWrapper : undefined}>
        <Gallery
          data={{
            images: data.images,
            descriptions: data.descriptions,
            total: data.total,
            altPrefix: data.altPrefix,
          }}
          variant={effectiveVariant}
          onImageClick={onImageClick}
          onSlideChange={onSlideChange}
          onSeeAllClick={onSeeAllClick}
          blur={blur}
          badgesSlot={data.badges}
          counterSlot={createMobileCounterSlot(onImageClick)}
          seeAllSlot={createDesktopSeeAllSlot(onSeeAllClick)}
          placeholderSlot={placeholderSlot}
          overlay={complianceOverlayElement}
          shareSlot={shareSlot}
        />
      </Container>
    </ResponsiveContainer>
  );
}

export { GALLERY_VARIANTS } from './types';
export type { GallerySectionProps, GallerySectionData, GalleryVariant } from './types';
export { useGallerySection, useGalleryWithBadges } from './use-gallery-section';
export { createMobileCounterSlot, createDesktopSeeAllSlot } from './gallery-slots';
